import './App.css';
import { useCallback, useEffect, useState } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation } from 'react-router-dom';
import authService, { OperatorSession } from './AuthService';
import api, { OperatorProxyRecord, OperatorProxySelectionRequired } from './MeshApi';

import Deployments from './Deployments';
import Certificates from './Certificates';
import DeleteHumans from './DeleteHumans';
import Callback from './Callback';
import Builds from './Builds';
import DebugSessions from './DebugSessions';
import Configuration from './Configuration';
import Admin from './Admin';
import SelectOperator from './SelectOperator';

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLoginButton, setShowLoginButton] = useState(false);
  // Every page below acts as a mesh operator, so the acting proxy and the organization it claims
  // authority from are resolved once here rather than on each page's first request.
  const [operatorSession, setOperatorSession] = useState<OperatorSession | null>(null);
  const [proxyChoices, setProxyChoices] = useState<OperatorProxyRecord[] | null>(null);
  const [operatorError, setOperatorError] = useState<string>('');
  // Survives the sign-out it describes: the reason has to outlive the tokens, or ending the
  // session would drop the operator back to a bare landing page with nothing said.
  const [sessionEndedMessage, setSessionEndedMessage] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const isReady = isLoggedIn && operatorSession !== null;

  const handleLogin = async () => {
    setSessionEndedMessage('');
    authService.login();
  };

  const handleLogout = () => {
    authService.logout();
    setIsLoggedIn(false);
    setShowLoginButton(true);
    setOperatorSession(null);
    setProxyChoices(null);
    setOperatorError('');
    setSessionEndedMessage('');
    navigate('/');
  };

  const resolveOperatorSession = useCallback(async () => {
    setOperatorError('');
    try {
      setOperatorSession(await api.ensureOperatorSession());
      setProxyChoices(null);
    } catch (error: any) {
      if (error instanceof OperatorProxySelectionRequired) {
        setProxyChoices(error.proxies);
      } else {
        console.log('Failed to resolve mesh-operator authority:', error);
        setOperatorError(error.toString());
      }
    }
  }, []);

  const handleSelectOperator = async (operatorProxy: string) => {
    setOperatorError('');
    try {
      setOperatorSession(await api.selectOperatorProxy(operatorProxy));
      setProxyChoices(null);
    } catch (error: any) {
      console.log('Failed to select operator proxy:', error);
      setOperatorError(error.toString());
    }
  };

  // Always offers the choice, rather than re-running the auto-select that would land on the same
  // proxy: an operator asking to switch has already been told which organization they are acting
  // for by the label next to this button.
  const handleSwitchOperator = async () => {
    setOperatorError('');
    try {
      const proxies = await api.listOperatorProxies();
      api.clearOperatorSession();
      setOperatorSession(null);
      setProxyChoices(proxies);
    } catch (error: any) {
      console.log('Failed to list operator proxies:', error);
      setOperatorError(error.toString());
    }
  };

  // The mesh can refuse a session mid-use - a reauthentication window closing is the case that
  // reaches this client - and the API layer ends it where it finds out. This is where that becomes
  // visible: without it the next page just fails again with the tokens already gone.
  useEffect(() => {
    authService.onSessionEnded = reason => {
      setIsLoggedIn(false);
      setShowLoginButton(true);
      setOperatorSession(null);
      setProxyChoices(null);
      setOperatorError('');
      setSessionEndedMessage(reason);
      navigate('/');
    };
    return () => {
      authService.onSessionEnded = null;
    };
  }, [navigate]);

  useEffect(() => {
    const checkLogin = async () => {
      await authService.loadConfig();
      const tokens = authService.getTokens();
      if (tokens) {
        setIsLoggedIn(true);
        setShowLoginButton(false);
        setSessionEndedMessage('');
        if (!tokens.operatorSession) {
          await resolveOperatorSession();
        } else {
          setOperatorSession(tokens.operatorSession);
        }
      } else {
        setShowLoginButton(true);
        setOperatorSession(null);
      }
    };
    checkLogin();
  }, [location, resolveOperatorSession]);

  return (
    <div>
      <header className="header">
        <nav className="nav-bar">
          <ul className="nav-list">
            <li>
              {isReady ? (
                <NavLink
                  to="/deployments"
                  className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}
                >
                  Deployments
                </NavLink>
              ) : (
                <span className="nav-button disabled">Deployments</span>
              )}
            </li>
            <li>
              {isReady ? (
                <NavLink to="/builds" className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}>
                  Builds
                </NavLink>
              ) : (
                <span className="nav-button disabled">Builds</span>
              )}
            </li>
            <li>
              {isReady ? (
                <NavLink
                  to="/debug_sessions"
                  className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}
                >
                  Debug Sessions
                </NavLink>
              ) : (
                <span className="nav-button disabled">Debug Sessions</span>
              )}
            </li>
            <li>
              {isReady ? (
                <NavLink
                  to="/certificates"
                  className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}
                >
                  Certificates
                </NavLink>
              ) : (
                <span className="nav-button disabled">Certificates</span>
              )}
            </li>
            <li>
              {isReady ? (
                <NavLink
                  to="/configuration"
                  className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}
                >
                  Configuration
                </NavLink>
              ) : (
                <span className="nav-button disabled">Builds</span>
              )}
            </li>
            <li>
              {isReady ? (
                <NavLink to="/admin" className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}>
                  Admin
                </NavLink>
              ) : (
                <span className="nav-button disabled">Admin</span>
              )}
            </li>
            <li>
              {isReady ? (
                <NavLink
                  to="/delete_humans"
                  className={({ isActive }) => (isActive ? 'nav-button active' : 'nav-button')}
                >
                  Delete Humans
                </NavLink>
              ) : (
                <span className="nav-button disabled">Delete Humans</span>
              )}
            </li>
          </ul>
        </nav>
        <div className="auth-buttons">
          {operatorSession && (
            <span className="operator-org" title="The organization this session claims mesh-operator authority from">
              {operatorSession.organizationName ?? 'Mesh operator'}
              <button className="operator-switch" type="button" onClick={handleSwitchOperator}>
                switch
              </button>
            </span>
          )}
          {showLoginButton && (
            <button className="meshin-button" onClick={handleLogin}>
              Meshin
            </button>
          )}
          {isLoggedIn && (
            <button className="logout-button" onClick={handleLogout}>
              Meshout
            </button>
          )}
        </div>
      </header>
      <main className="App">
        {sessionEndedMessage && <div className="error-message">{sessionEndedMessage}</div>}
        {/* The pages below all act as a mesh operator, so none of them is rendered until the
            acting proxy and its organization are known. Signed out, the routes still render: that
            is what serves /callback. */}
        {isLoggedIn && !isReady ? (
          proxyChoices ? (
            <SelectOperator proxies={proxyChoices} onSelect={handleSelectOperator} />
          ) : (
            <div className="form-container">
              {operatorError ? (
                <div className="error-message">{operatorError}</div>
              ) : (
                <div className="form-group">Resolving mesh-operator authority...</div>
              )}
            </div>
          )
        ) : (
          <Routes>
            <Route path="/" element={<div></div>} />
            <Route path="/deployments" element={<Deployments />} />
            <Route path="/builds" element={<Builds />} />
            <Route path="/debug_sessions" element={<DebugSessions />} />
            <Route path="/configuration" element={<Configuration />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/certificates" element={<Certificates />} />
            <Route path="/delete_humans" element={<DeleteHumans />} />
            <Route path="/callback" element={<Callback />} />
          </Routes>
        )}
      </main>
    </div>
  );
};

export default App;
